/**
 * Shared helpers for external ("off-platform") bookings on the client.
 *
 * These bookings are NOT Sharetribe transactions — they come from
 * `/api/external-bookings` as plain JSON with money in subunits — so none of the
 * `Money`-based helpers in util/currency.js apply directly. Keeping the formatting
 * and the labels here means the guest payment page, the reservation lists and the
 * income page all say the same thing.
 *
 * See docs/external-bookings.md.
 */

import { POOL } from './types';

// The lifecycle of an OFFER. Once the bañista converts it, the Sharetribe
// transaction owns every later state and the booking shows up natively.
export const EXTERNAL_BOOKING_STATUS = {
  PENDING: 'pending',
  CONVERTED: 'converted',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired',
};

// Only a pending offer is worth showing the host: once converted it appears as an
// ordinary reservation, and the other states are over.
export const ACTIVE_EXTERNAL_BOOKING_STATUSES = [EXTERNAL_BOOKING_STATUS.PENDING];

const STATUS_LABELS = {
  [EXTERNAL_BOOKING_STATUS.PENDING]: 'Pendiente de que reserve',
  [EXTERNAL_BOOKING_STATUS.CONVERTED]: 'Reservada',
  [EXTERNAL_BOOKING_STATUS.CANCELLED]: 'Cancelada',
  [EXTERNAL_BOOKING_STATUS.EXPIRED]: 'Enlace caducado',
};

export const externalBookingStatusLabel = status => STATUS_LABELS[status] || status || '—';

/**
 * What "Protección Cocopool" covers. Shown in the host's modal only: the bañista's page
 * and emails deliberately do not list it — they are being asked to pay, not sold on the
 * host's cover.
 */
const LIABILITY_INSURANCE_BULLET = 'Seguro de responsabilidad civil';

export const COCOPOOL_PROTECTION_BULLETS = [
  LIABILITY_INSURANCE_BULLET,
  'Cobro protegido y pago seguro',
  'Liquidación automática al anfitrión',
  'Atención al cliente antes, durante y después',
];

/**
 * The protection bullets for the chosen listing. Interiores are not insurable
 * (docs/host-insurance.md §7), so their offer does not promise the insurance.
 *
 * @param {boolean} insured
 * @returns {Array<string>}
 */
export const cocopoolProtectionBullets = insured =>
  insured
    ? COCOPOOL_PROTECTION_BULLETS
    : COCOPOOL_PROTECTION_BULLETS.filter(b => b !== LIABILITY_INSURANCE_BULLET);

/**
 * Format an amount given in subunits (cents).
 *
 * @param {Object} intl react-intl
 * @param {number} subunits
 * @param {string} [currency]
 * @returns {string}
 */
export const formatSubunits = (intl, subunits, currency = 'EUR') => {
  const amount = Number(subunits);
  if (!Number.isFinite(amount)) {
    return '—';
  }
  return intl.formatNumber(amount / 100, {
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Guest group as a human label: `"1-5 personas"`, or `"10 personas"` when the bounds
 * are equal, or `"51 o más personas"` for the open-ended top tier.
 *
 * External bookings store the group as bounds, exactly like a regular booking (where
 * they are the `line-item/guest-count-minimum` / `-maximum` quantities), so the two
 * read the same everywhere. 999 is the stored sentinel for `51_above` — see
 * `parseGuestRangeBounds` in server/api-util/lineItems.js.
 */
export const formatGuestRangeLabel = ({ guestCountMin, guestCountMax } = {}) => {
  const min = Number(guestCountMin);
  const max = Number(guestCountMax);
  if (!Number.isFinite(min) && !Number.isFinite(max)) {
    return '—';
  }
  if (Number.isFinite(max) && max >= 999) {
    return `${min} o más personas`;
  }
  if (!Number.isFinite(max) || min === max) {
    return `${min} ${min === 1 ? 'persona' : 'personas'}`;
  }
  return `${min}-${max} personas`;
};

export const isExternalBookingActive = booking =>
  !!booking && ACTIVE_EXTERNAL_BOOKING_STATUSES.includes(booking.status);

/**
 * The link the bañista needs in order to pay.
 *
 * Shared by the reservation row and the modal's post-create step, because the guest's
 * email is OPTIONAL: when the host doesn't know it, this link is the only delivery
 * mechanism — they copy it and send it over Instagram, WhatsApp or wherever the booking
 * was agreed. Returns null when there is no token, or during SSR.
 */
export const externalBookingPaymentLink = booking =>
  booking && booking.token && typeof window !== 'undefined'
    ? `${window.location.origin}/reserva-externa/${booking.token}`
    : null;

/**
 * A `wa.me` link that opens WhatsApp with the payment link already written out.
 *
 * Addressed to the guest when their phone is known, and left unaddressed otherwise so
 * the host picks the chat — the phone is optional too, and these bookings are often
 * agreed in a thread the host already has open.
 *
 * wa.me wants the number as DIGITS ONLY, including the country code: the stored value
 * is human-formatted (`+34 612 345 678`), so everything else is stripped.
 */
export const externalBookingWhatsappLink = booking => {
  const link = externalBookingPaymentLink(booking);
  if (!link) {
    return null;
  }
  const name = booking.guestName ? `¡Hola ${booking.guestName}! ` : '';
  const text = encodeURIComponent(
    `${name}Aquí tienes el enlace para completar el pago de tu reserva en Cocopool: ${link}`
  );
  const digits = String(booking.guestPhone || '').replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}?text=${text}` : `https://wa.me/?text=${text}`;
};

/**
 * Which listing the "Proteger reserva externa" modal opens on.
 *
 * A host with one space has nothing to choose. With several, the pool is preselected:
 * hosts protect pool bookings far more often than the other space types, so it saves
 * the extra click.
 *
 * Deliberately only when there is EXACTLY ONE pool. This choice decides which listing
 * gets a real booking at a price the host typed, so with two pools any pick would be an
 * arbitrary guess at the host's expense — better to leave it empty and let them choose.
 *
 * @param {Array} hostListings  `{ id, searchListingType }` as mapped by the duck
 * @returns {String}            listing id, or '' when the host must choose
 */
export const defaultExternalBookingListingId = (hostListings = []) => {
  if (hostListings.length === 1) {
    return hostListings[0].id;
  }
  const pools = hostListings.filter(l => l && l.searchListingType === POOL);
  return pools.length === 1 ? pools[0].id : '';
};
